<script setup lang="ts">
    import { ref, computed, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import Loading from '@shared/components/Loading.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { loadCategories } from '@views/Contact/Contact.access';
    import { type Category } from '@views/Contact/ContactTypes';
    import { preUnsubscribeFromCategory } from '../Lists.access';

    const props = defineProps<{
        show: boolean;
        listId: string;
        listName: string;
    }>();

    const emit = defineEmits<{
        close: [];
    }>();

    const categories = ref<Category[]>([]);
    const selectedCategory = ref<Category | null>(null);
    const submitting = ref(false);
    const loading = ref(false);

    watch(
        () => props.show,
        async (show) => {
            if (!show) return;
            selectedCategory.value = null;
            loading.value = true;

            try {
                categories.value = await loadCategories();
            } finally {
                loading.value = false;
            }
        }
    );

    const warningText = computed(() => {
        if (!selectedCategory.value) return '';
        return $Resources.PreUnsubscribeFromCategoryWarningText.replace(
            '{{CategoryName}}',
            `<strong>${selectedCategory.value.Name}</strong>`
        ).replace('{{ListName}}', `<strong>${props.listName}</strong>`);
    });

    function close() {
        selectedCategory.value = null;
        emit('close');
    }

    async function submit() {
        if (!selectedCategory.value || submitting.value) return;
        submitting.value = true;

        try {
            await preUnsubscribeFromCategory(props.listId, selectedCategory.value.CategoryId);
            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.PreUnsubscribeFromCategoryAlertSuccessText });
            close();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.PreUnsubscribeFromCategoryAlertErrorText });
        } finally {
            submitting.value = false;
        }
    }
</script>

<template>
    <Modal :show="props.show" :title="$Resources.PreUnsubscribeFromCategoryHeading" @close="close">
        <template #content>
            <p class="m-lg-b">{{ $Resources.PreUnsubscribeFromCategoryText }}</p>

            <Loading :show="loading || submitting" :fixed="true" />

            <div class="form-group">
                <FormCheckable
                    class="m-sm-b"
                    v-for="category in categories"
                    :key="category.CategoryId"
                    type="radio"
                    name="unsubscribeCategory"
                    :label="category.Name"
                    :checked="selectedCategory?.CategoryId === category.CategoryId"
                    @update:modelValue="selectedCategory = category">
                    <template v-if="category.Description" #label>
                        {{ category.Name }}
                        <small class="d-block" v-if="category.Description">{{ category.Description }}</small>
                    </template>
                </FormCheckable>
            </div>

            <BsAlert v-if="selectedCategory" :type="AlertTypes.Warning" :content="warningText" :dismissible="false" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!selectedCategory || submitting" @click="submit">
                {{ $Resources.PreUnsubscribeFromCategoryStartBtn }}
            </button>
        </template>
    </Modal>
</template>

